import { createContext, useContext, useEffect, useState } from "react";

const listeners = new Set<() => void>();
export function navigate(to: string, replace = false) {
  if (to === location.pathname + location.search) return;
  if (replace) history.replaceState(null, "", to);
  else history.pushState(null, "", to);
  listeners.forEach((l) => l());
  window.scrollTo({ top: 0 });
}
window.addEventListener("popstate", () => listeners.forEach((l) => l()));

/**
 * Page transition hook: `go()` hands the move to the transition (the running avatar), which calls `commit()` when the screen is covered.
 * The handler returns `false` when it does not want to animate (then `go` just navigates at once).
 */
type Transition = (to: string, commit: () => void) => boolean;
let transition: Transition | null = null;
export function setTransition(t: Transition | null) { transition = t; }
/** navigate because somebody pressed something (links, "Start spillet") – animated if a transition is installed */
export function go(to: string) {
  if (to === location.pathname + location.search) return;
  if (transition) {
    let ok = false;
    try { ok = transition(to, () => navigate(to)); } catch { ok = false; }
    if (ok) return;
  }
  navigate(to);
}

export function usePath(): string {
  const [p, setP] = useState(location.pathname);
  useEffect(() => {
    const l = () => setP(location.pathname);
    listeners.add(l);
    return () => void listeners.delete(l);
  }, []);
  return p;
}

export interface RouteMatch {
  params: Record<string, string>;
}
export function match(pattern: string, path: string): RouteMatch | null {
  const keys: string[] = [];
  const re = new RegExp("^" + pattern.replace(/:([a-zA-Z]+)/g, (_, k) => (keys.push(k), "([^/]+)")) + "/?$");
  const m = re.exec(path);
  if (!m) return null;
  const params: Record<string, string> = {};
  keys.forEach((k, i) => (params[k] = decodeURIComponent(m[i + 1]!)));
  return { params };
}

export function Link(props: {
  to: string; className?: string; children?: any; "aria-label"?: string; "aria-current"?: "page" | undefined; style?: Record<string, string | number | undefined>; onClick?: () => void; title?: string;
  /** passed straight to the <a> (tilt effect and the hover/focus preview on the game tiles) */
  "data-tilt"?: string; onPointerEnter?: (e: any) => void; onPointerLeave?: (e: any) => void; onFocus?: (e: any) => void; onBlur?: (e: any) => void;
}) {
  return (
    <a
      href={props.to}
      className={props.className}
      aria-label={props["aria-label"]}
      aria-current={props["aria-current"]}
      style={props.style}
      title={props.title}
      data-tilt={props["data-tilt"]}
      onPointerEnter={props.onPointerEnter}
      onPointerLeave={props.onPointerLeave}
      onFocus={props.onFocus}
      onBlur={props.onBlur}
      onClick={(e: any) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
        e.preventDefault();
        props.onClick?.();
        go(props.to);
      }}
    >
      {props.children}
    </a>
  );
}
const Ctx = createContext<null>(null);
void Ctx;
void useContext;
